/** setter 注册表：SetterType → 组件。属性面板据此渲染每个字段。 */
import type { ComponentType } from 'react';
import { ColorPicker, Input, InputNumber, Radio, Select, Slider, Switch, Space } from 'antd';
import type { SetterType } from '@h5/schema';
import type { FieldDesc } from '../schema-to-fields';
import { AssetInput } from './AssetInput';
import { FormFieldsSetter } from './FormFieldsSetter';
import { MultiAssetInput } from './MultiAssetInput';

/** 物料侧可在 meta 上扩展 multiple: true，表示值为 string[]。 */
const isMulti = (field: FieldDesc, value: unknown) =>
  (field.meta as { multiple?: boolean }).multiple === true || Array.isArray(value);

export interface SetterProps {
  value: unknown;
  onChange(v: unknown): void;
  field: FieldDesc;
  /** 该图层的全部 props，供 visible 等联动用 */
  allProps: Record<string, unknown>;
}

const str = (v: unknown) => (v === undefined || v === null ? '' : String(v));
const num = (v: unknown) => (typeof v === 'number' ? v : undefined);

export const FONT_OPTIONS = [
  { label: '系统默认', value: '' },
  { label: '苹方 / 微软雅黑', value: '"PingFang SC","Microsoft YaHei",sans-serif' },
  { label: '宋体', value: '"Songti SC",SimSun,serif' },
  { label: '楷体', value: '"Kaiti SC",KaiTi,serif' },
  { label: '黑体', value: '"Heiti SC",SimHei,sans-serif' },
  { label: '等宽', value: 'Menlo,Consolas,monospace' },
];

export const SETTERS: Record<SetterType, ComponentType<SetterProps>> = {
  input: ({ value, onChange, field }) => (
    <Input value={str(value)} placeholder={field.meta.placeholder} onChange={(e) => onChange(e.target.value)} />
  ),
  textarea: ({ value, onChange, field }) => (
    <Input.TextArea value={str(value)} placeholder={field.meta.placeholder} autoSize={{ minRows: 2, maxRows: 6 }} onChange={(e) => onChange(e.target.value)} />
  ),
  number: ({ value, onChange, field }) => (
    <InputNumber style={{ width: '100%' }} value={num(value)} min={field.min} max={field.max} step={field.step} onChange={(v) => onChange(v ?? 0)} />
  ),
  slider: ({ value, onChange, field }) => (
    <Space.Compact style={{ width: '100%', alignItems: 'center' }}>
      <Slider style={{ flex: 1, margin: '0 12px 0 4px' }} value={num(value) ?? field.min ?? 0} min={field.min ?? 0} max={field.max ?? 100} step={field.step ?? 1} onChange={(v) => onChange(v)} />
      <InputNumber size="small" style={{ width: 64 }} value={num(value)} min={field.min} max={field.max} step={field.step} onChange={(v) => onChange(v ?? 0)} />
    </Space.Compact>
  ),
  color: ({ value, onChange }) => (
    <ColorPicker
      value={str(value) || '#000000'}
      showText
      allowClear
      format="hex"
      onChangeComplete={(c) => onChange(c.toHexString())}
      onClear={() => onChange('transparent')}
    />
  ),
  select: ({ value, onChange, field }) => (
    <Select style={{ width: '100%' }} value={value as string} options={field.options} onChange={(v) => onChange(v)} />
  ),
  radio: ({ value, onChange, field }) => (
    <Radio.Group
      size="small"
      optionType="button"
      buttonStyle="solid"
      value={value}
      options={field.options?.map((o) => ({ label: o.label, value: o.value as string | number }))}
      onChange={(e) => onChange(e.target.value)}
    />
  ),
  switch: ({ value, onChange }) => <Switch checked={!!value} onChange={(v) => onChange(v)} />,
  image: ({ value, onChange, field }) => isMulti(field, value)
    ? <MultiAssetInput value={value} onChange={onChange} accept="image/*" />
    : <AssetInput value={str(value)} onChange={onChange} accept="image/*" preview="image" placeholder="图片地址" />,
  video: ({ value, onChange, field }) => isMulti(field, value)
    ? <MultiAssetInput value={value} onChange={onChange} accept="video/*" />
    : <AssetInput value={str(value)} onChange={onChange} accept="video/*" placeholder="视频地址" />,
  audio: ({ value, onChange, field }) => isMulti(field, value)
    ? <MultiAssetInput value={value} onChange={onChange} accept="audio/*" />
    : <AssetInput value={str(value)} onChange={onChange} accept="audio/*" placeholder="音频地址" />,
  link: ({ value, onChange, field }) => (
    <Input value={str(value)} placeholder={field.meta.placeholder ?? 'https://'} onChange={(e) => onChange(e.target.value)} />
  ),
  font: ({ value, onChange }) => (
    <Select
      style={{ width: '100%' }}
      value={str(value)}
      options={FONT_OPTIONS}
      showSearch
      onChange={(v) => onChange(v)}
    />
  ),
  formFields: ({ value, onChange }) => <FormFieldsSetter value={value} onChange={onChange} />,
};
